import { useMemo } from "react";

import { Column } from "@tanstack/react-table";
import { format } from "date-fns";
import { get, isEmpty, keyBy } from "lodash-es";

import {
  ProviderResource,
  Resource,
  ResourceSample,
} from "src/api/connectApi/types";
import { FlowVersion, SchemaTypes } from "src/api/flowTypes";
import { useFlowVersion } from "src/api/flowVersionQueries";
import {
  Dataset,
  DatasetColumn,
  DatasetPurpose,
  DatasetRow,
  DesiredType,
  DesiredTypeValues,
  GLOBAL_FEATURES_TYPE,
} from "src/api/types";
import { ConnectionDataSourcesValues } from "src/baseConnectionNode/types";
import { NODE_TYPE } from "src/constants/NodeTypes";
import { CustomConnectionConfig } from "src/customConnectionNode/types";
import {
  AvailableOutcomeColumns,
  useAvailableColumns,
  useAvailableIntegrationNodes,
} from "src/datasets/DatasetTable/hooks";
import { JSONValue } from "src/datasets/DatasetTable/types";
import {
  AvailableOutcomeColumn,
  getEnrichedSchemaInputFields,
  getEnrichedSchemaOutputFields,
  getOutcomeDatasetColumns,
} from "src/datasets/DatasetTable/utils";
import {
  expandEntitySchema,
  getEntityTemplate,
} from "src/datasets/dataTemplateUtils";
import { toastActions } from "src/design-system/Toast/utils";
import { findSchema } from "src/entities/entityView/utils";
import { useEntitySchemas } from "src/entities/queries";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { getEventTemplate } from "src/eventsCatalogue/utils";
import { useEntityRelatedFeatures } from "src/featureCatalogue/queries";
import { useIsCaseManagementEnabled } from "src/hooks/useIsCaseManagementEnabled";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { useOutcomeTypes } from "src/outcomes/queries";
import {
  useFlowContext,
  useWorkspaceContext,
} from "src/router/routerContextHooks";
import { SchemaOptions } from "src/router/SearchParams";
import {
  DecodedJsonRefPath,
  decodeJsonRefPath,
  isJsonRefPath,
  JsonRefPath,
  JsonRefPathPrefix,
} from "src/schema/jsonRefUtils";
import { CaseSchemaWithMetadata } from "src/schema/schemaEditor/CaseSchemaEditor";
import { isCaseSchema } from "src/schema/schemaEditor/utils";
import {
  PropertyUI,
  SchemaConverter,
  SchemaUI,
  SubSchemaUI,
} from "src/schema/schemaMappingUtils";
import { useParamsDecode } from "src/utils/useParamsDecode";

export type DatasetIssue =
  | {
      issueType: "missing-column";
      schemaType: DesiredType;
    }
  | {
      issueType: "type-mismatch";
      schemaType: DesiredType;
      datasetType: DesiredType;
      complexType?: DecodedJsonRefPath;
    }
  | { issueType: "extra-column"; datasetType: DesiredType };

export type DatasetIssues = Record<string, DatasetIssue>;

export const schemaTypeToDesiredType = (
  schemaType: SchemaTypes,
): DesiredType => {
  if (schemaType === "date-str") return DesiredTypeValues.date;
  else if (schemaType === "datetime-str") return DesiredTypeValues.datetime;
  else if (schemaType === "union") return DesiredTypeValues.any;
  else if (schemaType === "file[]") return DesiredTypeValues.files;
  else if (isJsonRefPath(schemaType)) return DesiredTypeValues.object;
  else return schemaType;
};

export const getRefSchema = (
  schemasDefs: SchemaUI["$defs"],
  ref: JsonRefPath,
): SchemaUI | SubSchemaUI => {
  const refName = ref.slice(JsonRefPathPrefix.length);
  const refSchema = get(schemasDefs, refName);

  if (!refSchema) {
    throw new Error(`Schema reference ${ref} not found`);
  }

  return refSchema;
};

export const EMPTY_ISSUES: DatasetIssues = {};

export const findCompatibilityIssues = (
  dataset: Dataset,
  schema?: SchemaUI,
): DatasetIssues => {
  if (!schema) return EMPTY_ISSUES;

  const issues: DatasetIssues = {};

  const datasetColumnsByName = keyBy(dataset.input_columns, "name");

  // Check that all columns in the schema are present in the dataset with correct type
  schema.properties.forEach((prop) => {
    const datasetColumn: DatasetColumn | null =
      datasetColumnsByName[prop.fieldName] ?? null;

    const schemaColumnType = schemaTypeToDesiredType(prop.type[0]);

    if (!datasetColumn) {
      if (prop.required) {
        issues[prop.fieldName] = {
          issueType: "missing-column",
          schemaType: schemaColumnType,
        };
      }
    } else if (
      !COMPATIBILITY_TYPE_MAP[datasetColumn.desired_type].includes(
        schemaColumnType,
      )
    ) {
      issues[prop.fieldName] = {
        issueType: "type-mismatch",
        schemaType: schemaColumnType,
        complexType: decodeJsonRefPath(prop.type[0]),
        datasetType: datasetColumn.desired_type,
      };
    }
  });

  // Check that all columns in the dataset are present in the schema
  dataset.input_columns.forEach((column) => {
    const schemaColumn = schema.properties.find(
      (prop) => prop.fieldName === column.name,
    );
    if (!schemaColumn) {
      issues[column.name] = {
        issueType: "extra-column",
        datasetType: column.desired_type,
      };
    }
  });

  // Return stable reference to issues if no issues are present
  return isEmpty(issues) ? EMPTY_ISSUES : issues;
};

const COMPATIBLE_ISSUES = ["extra-column", "type-mismatch"];
export const isDatasetCompatible = (
  dataset: Dataset,
  inputSchemas?: SchemaUI,
): boolean => {
  const datasetIssues = findCompatibilityIssues(dataset, inputSchemas);

  return Object.values(datasetIssues).every((issue) =>
    COMPATIBLE_ISSUES.includes(issue.issueType),
  );
};

// This is read as "if the column type is `key` then it is compatible
// with the schema type if it is among `COMPATIBILITY_TYPE_MAP[key]` "
const COMPATIBILITY_TYPE_MAP: Record<DesiredType, DesiredType[]> = {
  string: ["any", "string"],
  boolean: ["any", "boolean"],
  number: ["any", "number"],
  integer: ["any", "number", "integer"],
  object: ["any", "object"],
  array: ["any", "array"],
  datetime: ["any", "string", "datetime"],
  date: ["any", "string", "date"],
  any: ["any"],
  enum: ["any", "enum"],
  files: ["any", "array", "files"],
};

export const buildUiSchemas = (
  version: Pick<FlowVersion, "input_schema" | "output_schema">,
) => ({
  input:
    version.input_schema && Object.keys(version.input_schema).length !== 0
      ? SchemaConverter.beToUI(version.input_schema, SchemaOptions.Input)
      : undefined,
  output:
    version.output_schema && Object.keys(version.output_schema).length !== 0
      ? SchemaConverter.beToUI(version.output_schema, SchemaOptions.Output)
      : undefined,
});

export const useSchemas = (
  version: Pick<FlowVersion, "input_schema" | "output_schema"> | undefined,
) => {
  return useMemo(
    () =>
      buildUiSchemas({
        input_schema: version?.input_schema,
        output_schema: version?.output_schema,
      }),
    // Putting only the needed keys here instead of the whole version is a significant
    // reduction on-calculations when used with an object from react-query
    // (Nested object that are not changed maintain identity, changes bubble up though)
    [version?.input_schema, version?.output_schema],
  );
};

export const useInputColumnsFromSchema = (
  version: FlowVersion | undefined,
): DatasetColumn[] => {
  const { input: inputSchema } = useSchemas(version);

  if (!inputSchema) return [];

  return inputSchema.properties.map((property) => ({
    name: property.fieldName,
    desired_type: schemaTypeToDesiredType(property.type[0]),
    use_subflow_mocks: false,
  }));
};

export const useOutcomeColumnsFromOutcomeTypes = () => {
  const { flow } = useFlowContext();
  const { data: outcomeTypes } = useOutcomeTypes({
    flowId: flow.id,
  });

  return getOutcomeDatasetColumns(outcomeTypes ?? []).map((column) => ({
    name: column.columnName,
    desired_type: schemaTypeToDesiredType(column.property.type[0]),
    use_subflow_mocks: false,
  }));
};

export const separateFilenameExtension = (
  filename: string,
): { name: string; extension?: string } => {
  const RECOGNIZED_EXTENSIONS = ["json", "csv", "xlsx"];

  const filenameSegments = filename.split(".");

  if (filenameSegments.length < 2) {
    return { name: filename };
  }

  const possibleExtensionSegment = filenameSegments.at(-1)!;

  if (RECOGNIZED_EXTENSIONS.includes(possibleExtensionSegment.toLowerCase())) {
    return {
      name: filenameSegments.slice(0, -1).join("."),
      extension: possibleExtensionSegment,
    };
  } else {
    return { name: filename };
  }
};

export const isDatasetEmpty = (dataset: Dataset | undefined): boolean =>
  dataset?.input_columns.length === 0 &&
  dataset?.output_columns.length === 0 &&
  dataset?.mock_columns.length === 0 &&
  dataset?.outcome_columns.length === 0;

export const MOCK_EXTERNAL_DATA_DOCS_URL =
  "https://docs.taktile.com/testing-and-mocking/datasets-and-testing-1/testing/edit-datasets-and-resolve-incompatibility#external-mock-data";

export type DatasetIntegrationNode = {
  id: string;
  name: string;
  provider:
    | ProviderResource["provider"]
    | NODE_TYPE.FLOW_NODE
    | NODE_TYPE.LOOP_NODE
    | NODE_TYPE.READ_ENTITY_NODE
    | NODE_TYPE.AI_NODE_V2
    | NODE_TYPE.AGENT_NODE;
  resource: Resource | null;
  mediaKey: string | null;
  testingConfig: CustomConnectionConfig["testing"] | null;
  environmentsConfig: CustomConnectionConfig["environments_config"] | null;
  localSampleReports: ResourceSample[] | null;
  mockableChildNodes: DatasetIntegrationNode[] | null;
};

export const isIntegrationNodeWithLiveConnection = (
  node?: DatasetIntegrationNode,
): boolean => {
  if (node?.environmentsConfig) {
    // If environmentsConfig is present, this a CC Node with the new format
    const { authoring_mode_data_source } = node.environmentsConfig;

    return authoring_mode_data_source
      ? [
          ConnectionDataSourcesValues.production,
          ConnectionDataSourcesValues.sandbox,
        ].includes(authoring_mode_data_source)
      : false;
  }

  // For backwards compatibility on the old format of the CC node.
  return Boolean(node?.testingConfig?.use_fallback_live_connection);
};

export const DATASET_JOB_TIMEOUT =
  "We couldn't process this dataset under 15 minutes. Please try again.";

type TemplateField = {
  name: string;
  type: string;
  template?: JSONValue;
};

const getDefaultValueBasedOnType = (type: string): JSONValue => {
  switch (type) {
    case "string":
      return "";
    case "date":
    case "date-str":
      return format(new Date(), "yyyy-MM-dd");
    case "datetime":
    case "datetime-str":
      return new Date().toISOString();
    case "enum":
      return "";
    case "boolean":
      return false;
    case "number":
    case "integer":
      return 0;
    case "object":
      return {};
    case "array":
    case "files":
      return [];
    default:
      return "";
  }
};

const createCsvTemplateData = (
  inputFields: TemplateField[],
  mockFields: { name: string; subMocks?: { name: string }[] | null }[],
  outputFields: TemplateField[],
  outcomeFields: AvailableOutcomeColumn[],
): {
  flatTemplateRow: Record<string, JSONValue>;
  headers: Record<string, string>;
} => {
  const flatTemplateRow: Record<string, JSONValue> = {};
  const headers: Record<string, string> = {};

  inputFields.forEach((field) => {
    const key = `input.${field.name}`;
    const header = `request__data__${field.name}`;
    flatTemplateRow[key] = "";
    headers[key] = header;
  });

  mockFields.forEach((mock) => {
    if (mock.subMocks) {
      mock.subMocks.forEach((subMock) => {
        const key = `mock.${mock.name}.${subMock.name}`;
        const header = `external__data__${mock.name}__${subMock.name}`;
        flatTemplateRow[key] = "";
        headers[key] = header;
      });
    } else {
      const key = `mock.${mock.name}`;
      const header = `external__data__${mock.name}`;
      flatTemplateRow[key] = "";
      headers[key] = header;
    }
  });

  outputFields.forEach((field) => {
    const key = `output.${field.name}`;
    const header = `response__data__${field.name}`;
    flatTemplateRow[key] = "";
    headers[key] = header;
  });

  outcomeFields.forEach((field) => {
    const key = `outcome.${field.outcomeKey}.${field.property.fieldName}`;
    const header = `outcome__data__${field.outcomeKey}__${field.property.fieldName}`;
    flatTemplateRow[key] = "";
    headers[key] = header;
  });

  return { flatTemplateRow, headers };
};

type JsonTemplate = {
  request: {
    data: Record<string, JSONValue>;
  };
  external: {
    data: Record<string, JSONValue>;
  };
  response?: {
    data: Record<string, JSONValue>;
  };
  outcome?: {
    data: Record<string, JSONValue>;
  };
};
const createJsonTemplateData = (
  inputFields: TemplateField[],
  mockFields: { name: string; subMocks?: { name: string }[] | null }[],
  outputFields: TemplateField[],
  outcomeFields: AvailableOutcomeColumn[],
  hasCaseField = false,
) => {
  const jsonTemplate: JsonTemplate = {
    request: {
      data: {},
    },
    external: {
      data: {},
    },
  };

  if (outputFields.length > 0 || !hasCaseField) {
    jsonTemplate.response = {
      data: {},
    };
  }

  inputFields.forEach((field) => {
    jsonTemplate.request.data[field.name] =
      field.template || getDefaultValueBasedOnType(field.type);
  });

  mockFields.forEach((mock) => {
    jsonTemplate.external.data[mock.name] = {
      data: {},
      insights: {},
    };
  });

  if (jsonTemplate.response) {
    const responseData = jsonTemplate.response.data;
    outputFields.forEach((field) => {
      responseData[field.name] = getDefaultValueBasedOnType(field.type);
    });
  }

  const outcomesByKey = outcomeFields.reduce(
    (acc, field) => {
      if (!acc[field.outcomeKey]) {
        acc[field.outcomeKey] = {};
      }
      acc[field.outcomeKey][field.property.fieldName] =
        getDefaultValueBasedOnType(field.property.type[0]);
      return acc;
    },
    {} as Record<string, Record<string, JSONValue>>,
  );

  if (Object.keys(outcomesByKey).length > 0) {
    jsonTemplate.outcome = {
      data: outcomesByKey,
    };
  }

  return jsonTemplate;
};

const downloadDatasetTemplate = (content: string, fileType: "csv" | "json") => {
  const toastId = `template-download-${fileType}`;

  try {
    toastActions.loading({
      id: toastId,
      title: "Preparing your download...",
      description: "Creating template file...",
    });

    const mimeType = fileType === "csv" ? "text/csv" : "application/json";
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);

    toastActions.success({
      id: toastId,
      duration: Infinity,
      title: "Download ready",
      description: `Your ${fileType.toUpperCase()} template file is ready for download.`,
      actionText: "Click here to download",
      onActionClick: () => {
        const link = document.createElement("a");
        link.href = url;
        link.download = `dataset_template.${fileType}`;
        link.click();
        URL.revokeObjectURL(url);
      },
    });
  } catch {
    toastActions.failure({
      id: toastId,
      title: "Download failed",
      description: `Failed to create ${fileType.toUpperCase()} template file.`,
    });
  }
};

export const getSelectedValues = (
  value: JSONValue | null | undefined,
): string[] | undefined => {
  if (!value) return undefined;

  if (Array.isArray(value)) {
    return value.map((item) => JSON.stringify(item));
  }

  return [JSON.stringify(value)];
};

const createTemplateContent = (
  templateData: {
    availableInputColumns: TemplateField[];
    availableMockColumns: {
      mockFields: { name: string; subMocks?: { name: string }[] | null }[];
    };
    availableOutputColumns: TemplateField[];
    availableOutcomeColumns: AvailableOutcomeColumns;
    hasCaseField: boolean;
  },
  fileType: "csv" | "json",
): string => {
  if (fileType === "csv") {
    const { flatTemplateRow, headers } = createCsvTemplateData(
      templateData.availableInputColumns,
      templateData.availableMockColumns.mockFields,
      templateData.availableOutputColumns,
      templateData.availableOutcomeColumns.outcomeFields,
    );

    const headerRow = Object.keys(flatTemplateRow)
      .map((key) => headers[key])
      .join(",");
    const valueRow = Object.values(flatTemplateRow).join(",");
    return [headerRow, valueRow].join("\n");
  }

  const jsonTemplateRow = createJsonTemplateData(
    templateData.availableInputColumns,
    templateData.availableMockColumns.mockFields,
    templateData.availableOutputColumns,
    templateData.availableOutcomeColumns.outcomeFields,
    templateData.hasCaseField,
  );

  return JSON.stringify([jsonTemplateRow], null, 2);
};

const useTemplateData = () => {
  const { flow } = useFlowContext();
  const { version_id: flowVersionId } = useParamsDecode();
  const { data: flowVersion } = useFlowVersion(flowVersionId);
  const { workspace } = useWorkspaceContext();
  const featureGates = useWorkspaceFeatureGates();
  const { isEnabled: isCaseManagementEnabled } = useIsCaseManagementEnabled();

  const { data: schemasData } = useEntitySchemas({
    baseUrl: workspace.base_url!,
    options: { enabled: featureGates.entitiesEnabled },
  });

  const { data: eventTypes } = useEventSchemas();
  const integrationNodes = useAvailableIntegrationNodes(flowVersion);
  const schemas = useSchemas(flowVersion);
  const { data: features } = useEntityRelatedFeatures({
    options: {
      enabled: featureGates.featuresEventsEnabled,
    },
  });

  const createCaseTemplate = useMemo(() => {
    if (!isCaseManagementEnabled) {
      return null;
    }

    const caseProperty = schemas.input?.properties?.find(
      (prop) => prop.fieldName === "case",
    );
    if (caseProperty && flowVersion?.input_schema?.properties?.case) {
      const rawCaseSchema = flowVersion.input_schema.properties
        .case as CaseSchemaWithMetadata;

      return createCaseDataTemplate(rawCaseSchema);
    }

    return null;
  }, [schemas, flowVersion, isCaseManagementEnabled]);

  const emptyDataset = {
    name: "",
    flow_id: flow.id,
    source: "scratch" as const,
    purpose: "test" as DatasetPurpose,
    input_columns: [],
    output_columns: [],
    mock_columns: [],
    outcome_columns: [],
    id: "",
    created_at: "",
    updated_at: "",
    etag: "",
  };

  const {
    availableInputColumns,
    availableMockColumns,
    availableOutputColumns,
    availableOutcomeColumns,
  } = useAvailableColumns(emptyDataset, schemas, integrationNodes);

  const enrichedInputColumns = getEnrichedSchemaInputFields(schemas.input);
  const enrichedOutputColumns = getEnrichedSchemaOutputFields(schemas.output);

  const inputColumns = availableInputColumns.inputFields.map((field) => {
    const enrichedInput = enrichedInputColumns.find(
      (input) => input.name === field,
    );
    if (
      enrichedInput?.id &&
      enrichedInput?.type === "Entities" &&
      featureGates.entitiesEnabled
    ) {
      const relationSchema = findSchema(enrichedInput.id, schemasData);
      if (relationSchema) {
        const expandedSchema = expandEntitySchema(
          relationSchema,
          schemasData?.entities ?? [],
          4,
        );
        return {
          name: field,
          template: getEntityTemplate(
            expandedSchema,
            features ?? [],
            featureGates,
          ),
          type: enrichedInput.type,
        };
      }
    }
    if (
      enrichedInput?.type === "Events" &&
      featureGates.featuresEventsEnabled
    ) {
      const eventTypeData = eventTypes?.find(
        (event) => event.event_type === enrichedInput.id,
      );
      const properties = eventTypeData?.properties;
      return {
        name: field,
        template: getEventTemplate(
          properties,
          schemasData,
          features ?? [],
          featureGates,
        ),
        type: enrichedInput.type,
      };
    }

    if (isCaseManagementField(field, flowVersion, isCaseManagementEnabled)) {
      return {
        name: field,
        type: "case",
        template: createCaseTemplate,
      };
    }

    return {
      name: field,
      type:
        enrichedInput?.type ||
        (field === GLOBAL_FEATURES_TYPE && featureGates.featuresEventsEnabled
          ? "object"
          : "string"),
    };
  });

  const outputColumns = availableOutputColumns.outputFields.map((field) => {
    const enrichedOutput = enrichedOutputColumns.find(
      (output) => output.name === field,
    );
    return { name: field, type: enrichedOutput?.type || "string" };
  });

  const hasCaseField = inputColumns.some((col) => col.type === "case");

  return {
    availableInputColumns: hasCaseField
      ? inputColumns.filter((col) => col.name !== GLOBAL_FEATURES_TYPE)
      : inputColumns,
    availableMockColumns,
    availableOutputColumns: hasCaseField ? [] : outputColumns,
    availableOutcomeColumns,
    hasCaseField,
  };
};

export const useDownloadTemplate: () => (type: "csv" | "json") => void = () => {
  const templateData = useTemplateData();

  return (type: "csv" | "json") => {
    const content = createTemplateContent(templateData, type);
    downloadDatasetTemplate(content, type);
  };
};

export const isCaseManagementField = (
  fieldName: string,
  flowVersion: FlowVersion | undefined,
  isCaseManagementEnabled: boolean,
): boolean => {
  if (!isCaseManagementEnabled || fieldName !== "case") {
    return false;
  }

  if (!isCaseSchema(flowVersion?.input_schema)) {
    return false;
  }

  const rawCaseSchema = flowVersion?.input_schema?.properties
    ?.case as CaseSchemaWithMetadata;

  if (!rawCaseSchema) {
    return false;
  }

  return !!(
    (rawCaseSchema._template_metadata?.templateId &&
      rawCaseSchema._template_metadata?.versionId) ||
    rawCaseSchema.properties?.case_properties
  );
};

const getDefaultValueForType = (type: string): unknown => {
  switch (type) {
    case "string":
      return "";
    case "array":
      return [];
    case "object":
      return {};
    case "datetime":
    case "datetime-str":
      return new Date().toISOString();
    case "boolean":
      return false;
    case "number":
    case "integer":
      return 0;
    default:
      return "";
  }
};

export const createCaseDataTemplate = (
  rawCaseSchema: CaseSchemaWithMetadata,
): Record<string, unknown> => {
  const caseData: Record<string, unknown> = {};

  if (!rawCaseSchema.properties) {
    return caseData;
  }

  Object.entries(rawCaseSchema.properties).forEach(([key, property]) => {
    caseData[key] = generateValueFromSchemaProperty(property);
  });

  return caseData;
};

const generateValueFromSchemaProperty = (property: unknown): unknown => {
  if (!property || typeof property !== "object") {
    return getDefaultValueForType("string");
  }

  const prop = property as Record<string, unknown>;

  if (prop.enum && Array.isArray(prop.enum) && prop.enum.length > 0) {
    return prop.enum[0];
  }

  switch (prop.type) {
    case "string":
      if (prop.format === "date-time") {
        return new Date().toISOString();
      }
      return "";
    case "number":
    case "integer":
      return 0;
    case "boolean":
      return false;
    case "array":
      return [];
    case "object":
      if (prop.properties && typeof prop.properties === "object") {
        const nestedObj: Record<string, unknown> = {};
        Object.entries(prop.properties as Record<string, unknown>).forEach(
          ([nestedKey, nestedProperty]) => {
            nestedObj[nestedKey] =
              generateValueFromSchemaProperty(nestedProperty);
          },
        );
        return nestedObj;
      }
      return {};
    default:
      return getDefaultValueForType("string");
  }
};

type DatasetRowColumn = Column<DatasetRow, unknown>;
type ColumnWithCaseResource = DatasetRowColumn & {
  columnDef: { meta: { resource: CaseSchemaWithMetadata } };
};

export const isCaseInputColumn = (
  column: Column<DatasetRow, unknown>,
): column is ColumnWithCaseResource => {
  const columnName = column.columnDef.meta?.archetype?.name;

  return !!(
    columnName === "case" &&
    column.columnDef.meta?.resource &&
    "_template_metadata" in column.columnDef.meta.resource
  );
};

const SCHEMA_TYPE_DEFAULTS: Record<SchemaTypes, unknown> = {
  boolean: true,
  integer: 1,
  number: 1.5,
  string: "example",
  datetime: new Date(),
  date: new Date(),
  "date-str": "",
  "datetime-str": "",
  any: "",
  enum: [],
  array: [],
  object: {},
  union: {},
  "file[]": [],
};

const DRAFT_07_REF_PREFIX = JsonRefPathPrefix.replace("$defs", "definitions");

const extractDefNameFromRef = (ref: JsonRefPath): string | undefined => {
  if (ref.startsWith(JsonRefPathPrefix)) {
    return ref.slice(JsonRefPathPrefix.length);
  }
  if (ref.startsWith(DRAFT_07_REF_PREFIX)) {
    return ref.slice(DRAFT_07_REF_PREFIX.length);
  }
  return undefined;
};

const generateSampleForField = ({
  field,
  defs,
  typeDefaults,
  seenRefs,
}: {
  field: PropertyUI;
  defs: Record<string, SubSchemaUI>;
  typeDefaults: Record<SchemaTypes, unknown>;
  seenRefs: Set<string>;
}): unknown => {
  const fieldType = field.type[0];

  if (isJsonRefPath(fieldType)) {
    const defName = extractDefNameFromRef(fieldType);
    const defSchema = defName ? defs[defName] : undefined;

    if (!defName || !defSchema || seenRefs.has(fieldType)) {
      return {};
    }

    return generateSampleForFields({
      fields: defSchema.properties,
      defs,
      typeDefaults,
      seenRefs: new Set([...seenRefs, fieldType]),
    });
  }

  if (fieldType !== "enum") {
    return typeDefaults[fieldType];
  }

  if (field.enum && typeof field.enum[0]?.value === "string") {
    try {
      return JSON.parse(field.enum[0].value);
    } catch {
      return "";
    }
  }

  return typeDefaults["string"];
};

const generateSampleForFields = ({
  fields,
  defs,
  typeDefaults,
  seenRefs,
}: {
  fields: PropertyUI[];
  defs: Record<string, SubSchemaUI>;
  typeDefaults: Record<SchemaTypes, unknown>;
  seenRefs: Set<string>;
}): Record<string, unknown> =>
  Object.fromEntries(
    fields.map((field) => [
      field.fieldName,
      generateSampleForField({ field, defs, typeDefaults, seenRefs }),
    ]),
  );

export const generateSampleReportForSchema = ({
  schema,
  typeDefaultsOverrides,
}: {
  schema: SchemaUI | undefined;
  typeDefaultsOverrides?: Record<SchemaTypes, unknown>;
}): ResourceSample["sample"] => {
  const fields = schema?.properties ?? [];
  const defs = {
    ...(schema?.$defs ?? {}),
    ...(schema?.definitions ?? {}),
  };
  const typeDefaults = {
    ...SCHEMA_TYPE_DEFAULTS,
    ...(typeDefaultsOverrides ?? {}),
  };

  return generateSampleForFields({
    fields,
    defs,
    typeDefaults,
    seenRefs: new Set(),
  });
};
